import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';

type NodeType = 'field' | 'decision' | 'branch-container' | 'merge-point';

interface FlowNode {
	id: string;
	nodeType: NodeType;
	label: string;
	fieldType?: string;
	key?: string;
	level: number;
	hasCondition?: boolean;
	hideExpression?: string;
	children: FlowNode[];
	parent?: FlowNode;
	// For branching
	trueBranch?: FlowNode[];
	falseBranch?: FlowNode[];
	branchLabel?: string; // 'True' or 'False'
	isRepeater?: boolean;
	// Store original field config for details
	fieldConfig?: FormlyFieldConfig;
}

@Component({
	selector: 'nx-ahs-flow-chart',
	templateUrl: './flow-chart.component.html',
	styleUrl: './flow-chart.component.scss',
	standalone: false
})
export class FlowChartComponent implements OnChanges {
	@Input() fields: FormlyFieldConfig[] = [];

	flowNodes: FlowNode[] = [];
	selectedNode: FlowNode | null = null;
	showDetails = false;
	zoomLevel = 100;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['fields'] && this.fields) {
			this.buildFlowNodes();
			// Clear selection when fields change
			this.selectedNode = null;
			this.showDetails = false;
		}
	}

	selectNode(node: FlowNode): void {
		this.selectedNode = node;
		this.showDetails = true;
	}

	closeDetails(): void {
		this.selectedNode = null;
		this.showDetails = false;
	}

	isNodeSelected(node: FlowNode): boolean {
		return this.selectedNode?.id === node.id;
	}

	zoomIn(): void {
		if (this.zoomLevel < 200) {
			this.zoomLevel += 10;
		}
	}

	zoomOut(): void {
		if (this.zoomLevel > 30) {
			this.zoomLevel -= 10;
		}
	}

	resetZoom(): void {
		this.zoomLevel = 100;
	}

	fitToScreen(): void {
		this.zoomLevel = 75;
	}

	private buildFlowNodes(): void {
		this.flowNodes = this.parseFields(this.fields, 0);
	}

	private parseFields(fields: FormlyFieldConfig[], level: number, parent?: FlowNode): FlowNode[] {
		const nodes: FlowNode[] = [];

		fields.forEach((field, index) => {
			// Check if this field has a conditional expression
			if (field.hideExpression) {
				// Create a decision node for the condition
				const decisionNode: FlowNode = {
					id: `decision-${level}-${index}`,
					nodeType: 'decision',
					label: this.formatCondition(field.hideExpression as string),
					level: level,
					hideExpression: field.hideExpression as string,
					children: [],
					parent: parent,
					fieldConfig: field
				};

				// Create the field node that will be conditionally shown
				const conditionalFieldNode: FlowNode = {
					id: `node-${level}-${index}`,
					nodeType: 'field',
					label: this.getFieldLabel(field),
					fieldType: this.getFieldType(field),
					key: this.getFieldKey(field),
					level: level + 1,
					hasCondition: true,
					children: [],
					parent: decisionNode,
					isRepeater: field.type === 'repeater',
					fieldConfig: field
				};

				// Parse nested fields (fieldGroup or fieldArray)
				if (field.fieldGroup && field.fieldGroup.length > 0) {
					conditionalFieldNode.children = this.parseFields(field.fieldGroup, level + 2, conditionalFieldNode);
				} else if (field.fieldArray) {
					const fieldArrayFields = this.getFieldArrayFields(field.fieldArray);
					if (fieldArrayFields.length > 0) {
						conditionalFieldNode.children = this.parseFields(
							fieldArrayFields,
							level + 2,
							conditionalFieldNode
						);
					}
				} else if (field.type === 'mat-formly-nested-script' || field.type === 'nested-script') {
					// Parse nested scripts from props.scripts array
					conditionalFieldNode.children = this.parseNestedScripts(field, level + 2, conditionalFieldNode);
				}

				// Create branch container
				const branchContainer: FlowNode = {
					id: `branch-${level}-${index}`,
					nodeType: 'branch-container',
					label: '',
					level: level,
					children: [],
					parent: parent,
					trueBranch: [], // When condition is TRUE, field is HIDDEN
					falseBranch: [conditionalFieldNode] // When condition is FALSE, field is SHOWN
				};

				nodes.push(decisionNode, branchContainer);
			} else {
				// Regular field without condition
				const node: FlowNode = {
					id: `node-${level}-${index}`,
					nodeType: 'field',
					label: this.getFieldLabel(field),
					fieldType: this.getFieldType(field),
					key: this.getFieldKey(field),
					level: level,
					children: [],
					parent: parent,
					isRepeater: field.type === 'repeater',
					fieldConfig: field
				};

				// Parse nested fields (fieldGroup or fieldArray)
				if (field.fieldGroup && field.fieldGroup.length > 0) {
					node.children = this.parseFields(field.fieldGroup, level + 1, node);
				} else if (field.fieldArray) {
					const fieldArrayFields = this.getFieldArrayFields(field.fieldArray);
					if (fieldArrayFields.length > 0) {
						node.children = this.parseFields(fieldArrayFields, level + 1, node);
					}
				} else if (field.type === 'mat-formly-nested-script' || field.type === 'nested-script') {
					// Parse nested scripts from props.scripts array
					node.children = this.parseNestedScripts(field, level + 1, node);
				}

				nodes.push(node);
			}
		});

		return nodes;
	}

	private getFieldArrayFields(fieldArray: any): FormlyFieldConfig[] {
		// fieldArray can be a FormlyFieldConfig or a function
		const config = typeof fieldArray === 'function' ? fieldArray({}) : fieldArray;

		if (config && config.fieldGroup) {
			return config.fieldGroup;
		}
		return [];
	}

	private parseNestedScripts(field: FormlyFieldConfig, level: number, parent: FlowNode): FlowNode[] {
		const nodes: FlowNode[] = [];

		// Check if field has scripts array in props
		if (field.props && Array.isArray(field.props['scripts'])) {
			const scripts = field.props['scripts'] as any[];

			scripts.forEach((script, index) => {
				const scriptNode: FlowNode = {
					id: `script-${level}-${index}`,
					nodeType: 'field',
					label: script.name || `Script ${script.scriptId}`,
					fieldType: 'nested-script',
					key: `script-${script.scriptId}`,
					level: level,
					children: [],
					parent: parent,
					fieldConfig: {
						key: `script-${script.scriptId}`,
						type: 'nested-script',
						props: {
							...script,
							scriptId: script.scriptId,
							name: script.name,
							description: script.description
						}
					} as FormlyFieldConfig
				};

				nodes.push(scriptNode);
			});
		}

		return nodes;
	}

	private formatCondition(expression: string): string {
		if (!expression) return 'Condition';

		// Convert function string to readable format
		const strExpr = String(expression);

		// Try to extract the condition from common patterns
		// Pattern: function(field) { return expression; }
		const match = strExpr.match(/return\s+(.+?);?\s*}/);
		if (match) {
			return match[1].trim();
		}

		// If it's already a simple expression, return it
		if (strExpr.length < 50 && !strExpr.includes('function')) {
			return strExpr;
		}

		return 'Has Condition';
	}

	private getFieldLabel(field: FormlyFieldConfig): string {
		return field.props?.label || this.getFieldKey(field) || 'Unnamed Field';
	}

	private getFieldType(field: FormlyFieldConfig): string {
		if (typeof field.type === 'string') {
			return field.type.replace('mat-formly-', '');
		}
		return 'unknown';
	}

	private getFieldKey(field: FormlyFieldConfig): string {
		if (!field.key) return '';
		return Array.isArray(field.key) ? field.key.join('.') : String(field.key);
	}

	getFieldIcon(type: string): string {
		const iconMap: Record<string, string> = {
			input: 'text_fields',
			textarea: 'notes',
			select: 'arrow_drop_down_circle',
			check: 'check_box',
			radio: 'radio_button_checked',
			date: 'calendar_today',
			static: 'article',
			hidden: 'visibility_off',
			richtext: 'format_color_text',
			group: 'view_agenda',
			repeater: 'repeat',
			'nested-script': 'list_alt',
			setter: 'settings'
		};
		return iconMap[type] || 'help_outline';
	}

	getNodeColor(fieldType?: string): string {
		if (!fieldType) return '#ff9800'; // Default color for decision nodes

		const colorMap: Record<string, string> = {
			input: '#2196f3',
			textarea: '#03a9f4',
			select: '#00bcd4',
			check: '#009688',
			radio: '#4caf50',
			date: '#8bc34a',
			group: '#ff9800',
			repeater: '#ff5722',
			'nested-script': '#9c27b0',
			setter: '#673ab7'
		};
		return colorMap[fieldType] || '#757575';
	}

	getFieldConfigJson(node: FlowNode): string {
		if (!node.fieldConfig) return '{}';
		return JSON.stringify(node.fieldConfig, null, 2);
	}

	hasValidators(node: FlowNode): boolean {
		return !!(node.fieldConfig?.validators || node.fieldConfig?.asyncValidators);
	}

	hasExpressions(node: FlowNode): boolean {
		return !!(node.fieldConfig?.expressions || node.fieldConfig?.expressionProperties);
	}
}
